/**
 * Secure Token Storage Service
 *
 * Manages access tokens and refresh tokens with secure storage practices:
 * - Access tokens: Stored in memory (most secure)
 * - Refresh tokens: Stored in localStorage with encryption
 * - Expiration times: Tracked to determine when refresh is needed
 */

interface TokenData {
  accessToken: string
  refreshToken: string
  expiresAt: string
  refreshTokenExpiresAt: string
}

// In-memory storage for access token (cleared on page refresh)
let memoryAccessToken: string | null = null

// Storage keys
const STORAGE_KEYS = {
  REFRESH_TOKEN: 'adventurecore_refresh_token',
  EXPIRES_AT: 'adventurecore_token_expires',
  REFRESH_EXPIRES_AT: 'adventurecore_refresh_expires',
  ACCESS_TOKEN: 'adventurecore_token', // Legacy - keeping for compatibility
}

/**
 * Simple encryption for refresh token (basic obfuscation)
 * For production, consider using a more robust encryption library
 */
function encode(value: string): string {
  return btoa(encodeURIComponent(value))
}

function decode(value: string): string {
  try {
    return decodeURIComponent(atob(value))
  } catch {
    return ''
  }
}

export const tokenStorage = {
  /**
   * Store tokens after login or refresh
   */
  setTokens(data: TokenData): void {
    // Store access token in memory
    memoryAccessToken = data.accessToken

    // Store refresh token in localStorage (encoded)
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, encode(data.refreshToken))

    // Store expiration times
    localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, data.expiresAt)
    localStorage.setItem(STORAGE_KEYS.REFRESH_EXPIRES_AT, data.refreshTokenExpiresAt)

    // Legacy support - also store access token in localStorage for backward compatibility
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken)
  },

  /**
   * Get access token (from memory first, fallback to localStorage)
   */
  getAccessToken(): string | null {
    // Try memory first
    if (memoryAccessToken) {
      return memoryAccessToken
    }

    // Fallback to localStorage for backward compatibility
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
  },

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    const encoded = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
    if (!encoded) return null
    return decode(encoded)
  },

  /**
   * Get token expiration time
   */
  getExpiresAt(): string | null {
    return localStorage.getItem(STORAGE_KEYS.EXPIRES_AT)
  },

  /**
   * Get refresh token expiration time
   */
  getRefreshExpiresAt(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_EXPIRES_AT)
  },

  /**
   * Check if access token is expired or expiring soon (within 5 minutes)
   */
  isAccessTokenExpired(): boolean {
    const expiresAt = this.getExpiresAt()
    if (!expiresAt) return true

    const expiryTime = new Date(expiresAt).getTime()
    const currentTime = Date.now()
    const bufferTime = 5 * 60 * 1000 // 5 minutes

    return currentTime >= (expiryTime - bufferTime)
  },

  /**
   * Check if refresh token is expired
   */
  isRefreshTokenExpired(): boolean {
    const expiresAt = this.getRefreshExpiresAt()
    if (!expiresAt) return true

    const expiryTime = new Date(expiresAt).getTime()
    const currentTime = Date.now()

    return currentTime >= expiryTime
  },

  /**
   * Check if user has valid tokens
   */
  hasValidTokens(): boolean {
    return !!(this.getAccessToken() && !this.isAccessTokenExpired()) ||
           !!(this.getRefreshToken() && !this.isRefreshTokenExpired())
  },

  /**
   * Clear all tokens (logout)
   */
  clearTokens(): void {
    memoryAccessToken = null
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT)
    localStorage.removeItem(STORAGE_KEYS.REFRESH_EXPIRES_AT)
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
  },

  /**
   * Update only the access token (used during refresh)
   */
  updateAccessToken(token: string, expiresAt: string): void {
    memoryAccessToken = token
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token)
    localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, expiresAt)
  },
}
